import React, { useEffect, useState } from 'react';

import { Col, List, Row } from 'tea-component';
import './index.scss';

import Chart from './chart';

import { rankingThirdWords } from '../utils';
import { getWordCommentsSentiment } from '@/pages/opinion/services';
import CardCommon from '@/pages/opinion/components/CardCommon';
import { useIntl } from 'umi';

import SvgIcon from '@/pages/home/components/SvgIcon';
import { sentimentType } from '@op/utils';
import { sentimentIcon } from '@/pages/opinion/constants';
import { keywordRenderFunc } from '@/pages/opinion/utils/text';

export default function Component({
  keepParams,
  keywordCorrelationFilter,
  selects,
  language = 'origin',
}: {
  keepParams: any;
  keywordCorrelationFilter: any;
  selects: any;
  language: string;
}) {
  const { formatMessage } = useIntl();
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState(false);
  const [total, setTotal] = useState(0);

  const [page, setPage] = useState(1);

  const [comments, setComments] = useState<any[]>([]);
  const [sentimentMention, setSentimentMention] = useState({
    negative_mentions: 0,
    neutral_mentions: 0,
    positive_mentions: 0,
  });

  const pageSize = 30;

  const maps: any = {
    origin: 'word',
    zh: 'word_cn',
    en: 'word_en',
  };

  let position = 'behind';
  let keywordRenderWords: any[] = [];
  let words: any[] = [];
  let words_type: string = '';

  const key = maps[language || 'origin'];

  if (selects.third.word) {
    const getRankingThirdWords = rankingThirdWords(selects);
    words = getRankingThirdWords.map((item) => item.word);
    keywordRenderWords = getRankingThirdWords.map((item) => item[key]);
    words_type = 'third';
    position = selects.third.position;
  } else if (selects.second.word) {
    words = [selects.head.word, selects.second.word];
    keywordRenderWords = [selects.head[key], selects.second[key]];
    words_type = 'second';
    if (selects.second.position === 'before') {
      words = words.reverse();
    }
    position = selects.second.position;
  } else if (selects.head.word) {
    words = [selects.head.word];
    keywordRenderWords = [selects.head[key]];
    words_type = 'head';
    position = selects.head.position;
  }

  const fetchData = async (localParams: any = {}) => {
    const curPage = localParams.page || page;

    if (loading) return;
    if (total > 0 && (curPage - 1) * pageSize >= total) return;

    const {
      edition_unified_id,
      id_type,
      entity_type,
      start_time,
      end_time,
      date_type,
      sentiment,
      language,
    } = keepParams;

    const { pos, distance, frequency, correlation } = keywordCorrelationFilter;

    if (!edition_unified_id) {
      return;
    }

    if (!words_type) return;

    try {
      const params: any = {
        edition_unified_id,
        id_type,
        entity_type,
        start_time,
        end_time,
        date_type,
        sentiments: sentiment,
        languages: language,

        sort_item: 'comment_time',
        sort_model: 'desc',
        page: curPage,
        page_size: pageSize,

        words,
        words_type,

        position,
      };

      if (pos !== 'all') params.pos = pos;
      else delete params.pos;
      if (distance !== 'all') params.distance = Number(distance);
      else delete params.distance;
      params.correlation = correlation !== '' ? correlation : -9999;
      if (params.frequency !== '') params.frequency = frequency;
      else delete params.frequency;

      setLoading(true);

      const data = await getWordCommentsSentiment(params);

      setApiError(false);

      if (data) {
        const { list = [], sentiment_mentions = {}, total } = data;

        if (Array.isArray(list) && list.length > 0) {
          if (curPage === 1) {
            setComments(list);
          } else {
            setComments(comments.concat(...list));
          }
        }

        if (sentiment_mentions) {
          setSentimentMention(sentiment_mentions);
        }

        setTotal(total);
      }

      setLoading(false);
    } catch (e) {
      console.log('error', e);
      setApiError(true);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData({
      page: 1,
    });

    setTotal(0);
    setPage(1);
    setComments([]);
    setSentimentMention({
      negative_mentions: 0,
      neutral_mentions: 0,
      positive_mentions: 0,
    });
  }, [selects]);

  useEffect(() => {
    fetchData();
  }, [page]);

  const onScrollBottom = () => {
    setPage(page + 1);
  };

  const renderContent = (item: any) => {
    const maps: any = {
      origin: 'content',
      en: 'content_to_en',
      zh: 'content_to_zh',
    };

    const key = maps[language] || maps.origin;

    return keywordRenderFunc({
      text: item[key],
      keyword: keywordRenderWords,
    });
  };

  return (
    <Row className="dbo-correlation-commentsbox">
      <Col span={12} className="dbo-correlation-comments">
        <CardCommon
          loadingHeight={300}
          emptyHeight={300}
          title={formatMessage({ id: 'Comments' })}
          loading={loading && page === 1}
          empty={(apiError && page === 1) || !comments?.length}
        >
          <List
            split="divide"
            onScrollBottom={onScrollBottom}
            style={{ maxHeight: 300, overflow: 'auto' }}
          >
            {comments.map((item, index) => {
              return (
                <List.Item
                  className="dbo-correlation-comment-item"
                  key={`${item.comment_uin}-${index}`}
                >
                  <div className="tab-flex">
                    <SvgIcon
                      className="tab-icon"
                      type={
                        sentimentIcon[
                          sentimentType(item.sentiment_rating, true) as number
                        ]
                      }
                    />
                    {renderContent(item)}
                  </div>
                </List.Item>
              );
            })}
          </List>
        </CardCommon>
      </Col>
      <Col span={12} className="dbo-correlation-sentiment">
        <CardCommon
          title={formatMessage({ id: 'Sentiment Distribution' })}
          loading={loading && page === 1}
          loadingHeight={300}
          empty={
            !sentimentMention.negative_mentions &&
            !sentimentMention.neutral_mentions &&
            !sentimentMention.positive_mentions
          }
        >
          <Chart data={sentimentMention} />
        </CardCommon>
      </Col>
    </Row>
  );
}
